import React from 'react';
import classnames from 'classnames';
import ReactModal from 'react-modal';
import ModalHeader from './ModalHeader';
import { ModalPage } from './models/ModalPage';
import LoadingSpinner from '../LoadingSpinner';
import { useMeasure, usePrevious, useUpdateEffect } from 'react-use';
import { animateScroll as scroll, scroller } from 'react-scroll';
import useBackButtonOverride from '../BackButtonOverride/useBackButtonOverride';
import noop from '@gik/core/utils/noop';
import bemBlock from '@gik/core/utils/bemBlock';
import { ArrowButtonNext, ArrowButtonPrev } from '../ArrowButton';

export interface ModalProps {
  isOpen: boolean;
  pageNumber?: number;
  pages?: ModalPage[];
  padding?: boolean;
  isLoading?: boolean;
  centeredTitle?: boolean;
  closable?: boolean;
  className?: string;
  overlayClassName?: string;
  header?: React.ReactNode;
  footer?: React.ReactNode;
  overlay?: JSX.Element;
  shouldCloseOnEsc?: boolean;
  shouldCloseOnBack?: boolean;
  shouldCloseOnOverlayClick?: boolean;
  disableBackdrop?: boolean;
  shouldReturnFocusAfterClose?: boolean;
  contentRef?: React.MutableRefObject<HTMLDivElement>;
  contentId?: string;
  headerId?: string;
  innerClassName?: string;
  innerStyle?: React.CSSProperties;
  openAnimationLength?: number;
  closeAnimationLength?: number;
  bodyOpenClassName?: string;
  /**
   * Scroll content to top, bottom or element with the id
   */
  autoScrollContentTo?: 'top' | 'bottom' | { id: string };
  portal?: () => HTMLElement;

  onClose?(): void;

  onAfterClose?(): void;

  onBack?(): void;

  onHeaderRef?(ref: HTMLDivElement): void;

  allowIntercom?: boolean;
}

export default function Modal({
  isOpen,
  pageNumber = 1,
  padding = true,
  pages,
  isLoading,
  closable = true,
  className,
  overlayClassName,
  header,
  footer,
  overlay,
  centeredTitle,
  shouldCloseOnEsc = true,
  shouldCloseOnBack = true,
  shouldCloseOnOverlayClick = false,
  disableBackdrop,
  shouldReturnFocusAfterClose = true,
  contentRef,
  contentId,
  headerId,
  closeAnimationLength = 1000,
  openAnimationLength = 1000,
  autoScrollContentTo,
  bodyOpenClassName,
  innerStyle,
  innerClassName,
  portal,
  onClose = noop,
  onAfterClose = noop,
  onBack,
  onHeaderRef,
  allowIntercom = false,
}: React.PropsWithChildren<ModalProps>) {
  let _contentRef: HTMLDivElement;
  const bem = bemBlock('modal');

  const disableBackButtonOverride = useBackButtonOverride(isOpen, handleClose, shouldCloseOnBack);

  const [isReady, setIsReady] = React.useState<boolean>(false);
  const [isClosing, setClosing] = React.useState<boolean>(false);
  const [scrolledToTop, setScrolledToTop] = React.useState<boolean>(false);
  const [scrolledToBottom, setScrolledToBottom] = React.useState<boolean>(false);

  const prevIsOpen = usePrevious(isOpen);
  React.useEffect(() => {
    if (prevIsOpen && !isOpen) {
      // modal was closed through header or some other button (not the browser back button)
      disableBackButtonOverride();
      setClosing(true);
      setTimeout(handleAfterClose, closeAnimationLength);
    }
  }, [isOpen, prevIsOpen]);

  React.useEffect(() => {
    _updateMoreContentIndicator();

    // we need one render cycle before rendering the contents so they have immediate access to the ref objects
    setTimeout(() => {
      setIsReady(true);
    });
  }, []);

  const scrollContainerRef = React.useRef<HTMLDivElement>(null);

  // FIXME: remove 'any' type
  // eslint-disable-next-line
  const hasAutoScrollIdProp = (autoScroll: any): autoScroll is { id: string } => {
    return typeof autoScroll?.id === 'string';
  };
  const _autoScrollContentTo = hasAutoScrollIdProp(autoScrollContentTo) ? autoScrollContentTo.id : autoScrollContentTo;

  React.useEffect(() => {
    if (!isOpen || !_autoScrollContentTo || !scrollContainerRef?.current) {
      return;
    }

    const autoScrollOptions = {
      duration: 300,
      smooth: true,
      container: scrollContainerRef.current,
    };

    switch (_autoScrollContentTo) {
      case 'top':
        scroll.scrollToTop(autoScrollOptions);
        break;
      case 'bottom':
        scroll.scrollToBottom(autoScrollOptions);
        break;
      default:
        scroller.scrollTo(_autoScrollContentTo, autoScrollOptions);
    }
  }, [isOpen, _autoScrollContentTo, scrollContainerRef?.current]);

  function handleAfterClose() {
    setClosing(false);
    if (onAfterClose) {
      onAfterClose();
    }
    const appLayoutEl = document.getElementsByClassName('gik-app-layout')[0];
    appLayoutEl?.classList.remove('gik-blur');
  }

  function handleClose() {
    if (onClose) {
      onClose();
    }
  }

  function handleAfterOpen() {
    const appLayoutEl = document.getElementsByClassName('gik-app-layout')[0];
    appLayoutEl?.classList.add('gik-blur');
  }

  function handleArrowPrev() {
    if (currentPage?.onArrowPrev) {
      currentPage.onArrowPrev();
    }
  }

  function handleArrowNext() {
    if (currentPage?.onArrowNext) {
      currentPage?.onArrowNext();
    }
  }

  const [modalRef] = useMeasure();

  useUpdateEffect(() => {
    _updateMoreContentIndicator();
    setTimeout(() => _updateMoreContentIndicator(), Math.max(closeAnimationLength, openAnimationLength));
  });

  const _updateMoreContentIndicator = () => {
    if (!_contentRef) {
      return;
    }

    const _scrolledToTop = _contentRef.scrollTop === 0;
    const _scrolledToBottom = _contentRef.scrollTop + _contentRef.clientHeight > _contentRef.scrollHeight - 1;

    if (_scrolledToTop != scrolledToTop || _scrolledToBottom != scrolledToBottom) {
      setScrolledToTop(_scrolledToTop);
      setScrolledToBottom(_scrolledToBottom);
    }
  };

  const currentPage = pages[pageNumber - 1];

  const _footer = currentPage.footer || footer;

  const blockClasses = classnames([
    `modals modal-v2 gik-modal`,
    { closing: isClosing },
    { 'gik-modal--padded': padding },
    { 'gik-modal--hidden-title': !currentPage.title },
    { 'gik-modal--has-footer': _footer },
    className || '',
  ]);

  const dialogsBem = bemBlock('dialogs');

  const modalProps = {
    parentSelector: portal || undefined,
  };

  const innerProps = {
    className: bem('inner', null, [innerClassName]),
    style: innerStyle,
  };

  const finalOverlayClassName = bem(
    'overlay',
    [],
    [
      overlayClassName,
      { 'backdrop-disabled': disableBackdrop },
      { 'backdrop-enabled': !disableBackdrop },
      { closing: isClosing },
    ]
  );
  if (!isReady) return null;

  const Component = (
    <ReactModal
      contentRef={modalRef}
      isOpen={isOpen || prevIsOpen || isClosing}
      contentLabel={currentPage.title}
      ariaHideApp={false}
      shouldCloseOnEsc={closable && shouldCloseOnEsc}
      shouldCloseOnOverlayClick={closable && shouldCloseOnOverlayClick}
      shouldFocusAfterRender={true}
      shouldReturnFocusAfterClose={shouldReturnFocusAfterClose}
      onRequestClose={handleClose}
      onAfterOpen={handleAfterOpen}
      bodyOpenClassName={classnames([
        'ReactModal__Body--open', // default classname
        bodyOpenClassName || '',
      ])}
      // style={{
      //   content: innerStyle,
      // }}
      className={blockClasses}
      overlayClassName={finalOverlayClassName}
      portalClassName={dialogsBem(null, [{ 'allow-intercom': allowIntercom }])}
      {...modalProps}
    >
      {header !== undefined ? (
        header
      ) : (
        <ModalHeader
          closable={closable}
          centeredTitle={centeredTitle}
          title={currentPage.title}
          content={currentPage.headerContent}
          onClose={handleClose}
          onBack={onBack}
          onRef={onHeaderRef}
          id={headerId}
        />
      )}
      <div className={`more-content-indicator top ${!scrolledToTop ? 'visible' : ''}`} />
      <div
        className={bem('content-wrapper')}
        ref={element => {
          _contentRef = element;
          if (contentRef) {
            contentRef.current = element;
          }
          if (scrollContainerRef) {
            scrollContainerRef.current = element;
          }
          _updateMoreContentIndicator();
        }}
        onScroll={_updateMoreContentIndicator}
        id={contentId}
      >
        {currentPage.pageArrows && (
          <ArrowButtonPrev
            className={bem('arrow-prev')}
            disabled={currentPage.pageArrowPrevDisabled}
            onClick={handleArrowPrev}
          />
        )}
        {currentPage.pageArrows && (
          <ArrowButtonNext
            className={bem('arrow-next')}
            disabled={currentPage.pageArrowNextDisabled}
            onClick={handleArrowNext}
          />
        )}

        <div className={`content ${isLoading || !isReady ? 'loading' : ''}`}>
          {isLoading || !isReady ? <LoadingSpinner center /> : currentPage.content}
        </div>
      </div>
      <div className={`more-content-indicator bottom ${!!_footer && !scrolledToBottom ? 'visible' : ''}`} />
      {_footer}

      {overlay ? <div className="modal-overlay">{overlay}</div> : null}
    </ReactModal>
  );

  return Component;
}
